/**
 * SPA root + router.
 *
 * Phase 14 Stream A swapped path-string-matching for `react-router-dom`
 * with a shared <AppShell/> layout. Routing is two-layered:
 *
 *   1. <RequireAuthOrLanding/> wrapper — when there's no session it
 *      renders the calm <LandingPage/> on `/` (issue 002 — closes the
 *      incognito-shim brand leak) and redirects to /sign-in for every
 *      other path. Authenticated visitors fall through to <Outlet/>.
 *   2. <AppShell/> layout — top nav + breadcrumbs + main outlet.
 *      Mounted as a Layout Route so all authenticated pages share it.
 *
 * Phase 14 Stream C added Clerk wiring. When `VITE_CLERK_PUBLISHABLE_KEY`
 * is set on the build, `<ClerkProvider>` wraps the app and the auth
 * context delegates to Clerk's `getToken()`. With no key, the dev-shim
 * auth from Stream A keeps working for local + preview deploys. The
 * dev-shim is now route-gated to `/demo` (see `lib/AuthContext.tsx`)
 * so a leftover dev id can no longer auto-sign-in a fresh visitor.
 */
import { ClerkProvider, SignIn, SignUp } from "@clerk/clerk-react";
import { Suspense, lazy, useEffect, useRef, useState } from "react";
import {
  BrowserRouter,
  Link,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";

import { useWorkspaces } from "./api/useWorkspaces";
import { AccountCacheGuard } from "./components/AccountCacheGuard";
import { AccountDebug } from "./components/AccountDebug";
import { AccountSwitchNotice } from "./components/AccountSwitchNotice";
import { AppShell } from "./components/AppShell";
import { AppUpdateStrip } from "./components/AppUpdateStrip";
import { BreadcrumbProvider, useBreadcrumbs } from "./components/Breadcrumbs";
import { NotFoundPage } from "./components/NotFoundPage";
import { RequireDisplayName } from "./components/RequireDisplayName";
import { ScrollToTopOnRouteChange } from "./components/ScrollToTopOnRouteChange";
import { DemoPhoneFrame } from "./components/demo/DemoPhoneFrame";
import { Button, Skeleton, TooltipProvider } from "./components/ui";
import { AuthProvider, useAuth } from "./lib/AuthContext";
import { CHOOSE_ACCOUNT_PATH, afterSignOutPath } from "./lib/accountSwitch";
import { resolveWelcomeGateRole } from "./lib/activeWorkspace";
import { clerkPublishableKey, hasClerk } from "./lib/clerk";
import { DEMO_PHONE_BASENAME, inDemoPhoneFrame } from "./lib/demoPhone";
import { clearDemoEnded, hasStoredDemoSession, isDemoEnded } from "./lib/demoSession";
import {
  CONNECT_TELEGRAM_PATH,
  consumeReturnTo,
  returnTargetFromSearch,
  safeReturnTarget,
  signInPathFor,
  stashReturnTo,
} from "./lib/returnTo";
import {
  type SignupIntent,
  consumeSignupIntent,
  peekSignupIntent,
  stashSignupIntent,
} from "./lib/signupIntent";
import {
  BlocksEditorRoute,
  CapacityRoute,
  CycleIndexRoute,
  CyclePageRoute,
  LegacyCycleDashboardRoute,
  LegacySolveRedirect,
  SubmitRoute,
} from "./routes/CycleRoutes";
import { DemoAccessGate } from "./routes/DemoAccessGate";
import { DemoIndustryPage } from "./routes/DemoIndustryPage";
import { DemoPage } from "./routes/DemoPage";
import { DemoScenarioPage } from "./routes/DemoScenarioPage";
import { DemoStartPage } from "./routes/DemoStartPage";
import { HomePage } from "./routes/HomePage";
import { InviteAcceptPage } from "./routes/InviteAcceptPage";
import { InvitePreviewPage } from "./routes/InvitePreviewPage";
import { JoinPage } from "./routes/JoinPage";
import { LandingPage } from "./routes/LandingPage";
import { OnboardingPage } from "./routes/OnboardingPage";
import { ProblemPage } from "./routes/ProblemPage";
import { StartPage } from "./routes/StartPage";
import { UserPicker } from "./routes/UserPicker";
import { ChooseAccountPage } from "./routes/auth/ChooseAccountPage";
import { SignInPage } from "./routes/auth/SignInPage";
import { SignUpPage } from "./routes/auth/SignUpPage";
import { SsoCallbackPage } from "./routes/auth/SsoCallbackPage";
import { DataHandlingPage } from "./routes/features/DataHandlingPage";
import { FeaturePage } from "./routes/features/FeaturePage";
import { HowItWorksPage } from "./routes/features/HowItWorksPage";
import { PricingPage } from "./routes/features/PricingPage";
import { MeWelcome, readPrivacyDismissed } from "./routes/me/MeWelcome";
// Issue #753 — task-mode SPA shell fork (Phase 0, dark).
import {
  MapHomePage,
  MapHomePageDashboard,
  MapHomeRedirect,
  MapMembersPage,
  MapSettingsPage,
} from "./taskmap/MapHomePage";
import { TelegramMiniApp } from "./telegram/TelegramMiniApp";
import { isMiniAppPath } from "./telegram/telegramSdk";

// WS6 OPT-2 (refs #823) — lazily load the heavy route bodies that used
// to be hoisted into the eager entry chunk. Each becomes its own
// on-demand chunk fetched only when the route is first visited. The
// components use named exports, so the dynamic import is mapped to the
// `{ default }` shape `React.lazy` expects — mirrors the existing
// pattern in `routes/CycleRoutes.tsx`. The eager imports above
// (auth/onboarding/landing critical path + the always-mounted taskmap
// shell + the already-lazy CycleRoutes wrappers) stay eager.
//
// Behaviour is identical: same components, same routes — only the
// load timing changes (route-level code-splitting), so route
// resolution, breadcrumbs, gates and query behaviour are unaffected.
//
// `/__styleguide` — design-system viewing surface (Claude design pipeline
// PoC, docs/design/claude-design-pipeline.md). Public + lazy; renders the
// ui/* primitives + token board so flipping the theme picker reskins the
// whole system at a glance. Carries no team data.
const StyleguideRoute = lazy(() =>
  import("./routes/StyleguideRoute").then((m) => ({ default: m.StyleguideRoute })),
);
const PrivacyPage = lazy(() =>
  import("./routes/LegalPages").then((m) => ({ default: m.PrivacyPage })),
);
const TermsPage = lazy(() => import("./routes/LegalPages").then((m) => ({ default: m.TermsPage })));
const MeAudit = lazy(() => import("./routes/MeAudit").then((m) => ({ default: m.MeAudit })));
const MeComply = lazy(() => import("./routes/MeComply").then((m) => ({ default: m.MeComply })));
const MeForecast = lazy(() =>
  import("./routes/MeForecast").then((m) => ({ default: m.MeForecast })),
);
const MeInsights = lazy(() =>
  import("./routes/MeInsights").then((m) => ({ default: m.MeInsights })),
);
const ModulePage = lazy(() =>
  import("./routes/ModulePage").then((m) => ({ default: m.ModulePage })),
);
const TeamAuditRedirect = lazy(() =>
  import("./routes/team/oversight/TeamAuditRedirect").then((m) => ({
    default: m.TeamAuditRedirect,
  })),
);
const TeamForecastPage = lazy(() =>
  import("./routes/TeamForecastPage").then((m) => ({ default: m.TeamForecastPage })),
);
const TeamMoodPage = lazy(() =>
  import("./routes/TeamMoodPage").then((m) => ({ default: m.TeamMoodPage })),
);
const TeamOutcomesPage = lazy(() =>
  import("./routes/TeamOutcomesPage").then((m) => ({ default: m.TeamOutcomesPage })),
);
// #1070 (WS-A) → #1959 — the team's Solves page (the history + the date
// line). #2030 — its address is `/solves`; `/timeline` redirects there.
const TeamTimelinePage = lazy(() =>
  import("./routes/TeamTimelinePage").then((m) => ({ default: m.TeamTimelinePage })),
);
const TeamSolvesRedirect = lazy(() =>
  import("./routes/TeamTimelinePage").then((m) => ({ default: m.TeamSolvesRedirect })),
);
const TeamPage = lazy(() => import("./routes/TeamPage").then((m) => ({ default: m.TeamPage })));
// #1956 — Team → Roster IS the roster: one page per team, a window on the
// team's date line (`?from&to&preset`). The old range view
// (`/schedule/range`) and stacked-weeks page (`/roster/weeks`) redirect
// here, as does every `/cycles/:id…` link (`<CyclePage/>`).
const TeamRosterPage = lazy(() =>
  import("./routes/team/TeamRosterPage").then((m) => ({ default: m.TeamRosterPage })),
);
// #2675, #2797 — the old address of the station view (`/teams/:teamId/floor`). The station
// view is the Floor row choice of the roster page; this route redirects there. Behind the
// `station_view` switch.
const TeamFloorRedirect = lazy(() =>
  import("./routes/team/TeamFloorRedirect").then((m) => ({ default: m.TeamFloorRedirect })),
);
// #2903 — the Team page (fifth lead entry): Stations, Access, Activity.
const TeamOversightPage = lazy(() =>
  import("./routes/team/oversight/TeamPage").then((m) => ({ default: m.TeamOversightPage })),
);
const TeamPageIndex = lazy(() =>
  import("./routes/team/oversight/TeamPage").then((m) => ({ default: m.TeamPageIndex })),
);
const StationsTab = lazy(() =>
  import("./routes/team/oversight/StationsTab").then((m) => ({ default: m.StationsTab })),
);
const AccessTab = lazy(() =>
  import("./routes/team/oversight/AccessTab").then((m) => ({ default: m.AccessTab })),
);
const ActivityTab = lazy(() =>
  import("./routes/team/oversight/ActivityTab").then((m) => ({ default: m.ActivityTab })),
);
const TeamRosterRangeRedirect = lazy(() =>
  import("./routes/team/TeamRosterRedirects").then((m) => ({
    default: m.TeamRosterRangeRedirect,
  })),
);
// #820 — R2.3 full members-management page. Additive to the team-home
// members sidebar; reachable from the sidebar's "Manage members" link
// + the AppShell "Members" nav entry. Supersedes the legacy
// `<TeamMembersAliasRedirect/>` (which bounced this URL to team home).
// #2048 — it is the People page now: the members list plus, as a tab, the
// manpower planning view (`?view=over-time`).
const TeamInsightsPage = lazy(() =>
  import("./routes/team/TeamInsightsPage").then((m) => ({ default: m.TeamInsightsPage })),
);
const PeoplePage = lazy(() =>
  import("./routes/team/PeoplePage").then((m) => ({ default: m.PeoplePage })),
);
// #1282 — manpower planning dashboard (FTE-weighted headcount heatmap
// by tier per week) + the person profile node (effective-dated FTE /
// tier timeline). Lead surfaces; component-gated on `manpower_planning`.
// #2048 — `/manpower` redirects to the People page's Over time tab.
const ManpowerToPeopleRedirect = lazy(() =>
  import("./routes/team/PeoplePage").then((m) => ({ default: m.ManpowerToPeopleRedirect })),
);
const MemberProfilePage = lazy(() =>
  import("./routes/team/MemberProfilePage").then((m) => ({ default: m.MemberProfilePage })),
);
const TeamRetrospectivePage = lazy(() =>
  import("./routes/TeamRetrospectivePage").then((m) => ({ default: m.TeamRetrospectivePage })),
);
const TeamSetupLayout = lazy(() =>
  import("./routes/TeamSetupLayout").then((m) => ({ default: m.TeamSetupLayout })),
);
const TeamWorkflowsPage = lazy(() =>
  import("./routes/TeamWorkflowsPage").then((m) => ({ default: m.TeamWorkflowsPage })),
);
// #1665 — the member phone app (design §2/§3): one D1 frame owning
// every `/me/*` route except `/me/welcome`. Lazy like every heavy
// route body; the calendar/tab internals ride in the same chunk.
const MemberAppLayout = lazy(() =>
  import("./memberapp/MemberAppLayout").then((m) => ({ default: m.MemberAppLayout })),
);
const MemberCalendarTab = lazy(() =>
  import("./memberapp/tabs/CalendarTab").then((m) => ({ default: m.CalendarTab })),
);
const MemberInboxTab = lazy(() =>
  import("./memberapp/tabs/InboxTab").then((m) => ({ default: m.InboxTab })),
);
const MemberTasksTab = lazy(() =>
  import("./memberapp/tabs/TasksTab").then((m) => ({ default: m.TasksTab })),
);
const WhosOnScreen = lazy(() =>
  import("./memberapp/calendar/WhosOnScreen").then((m) => ({ default: m.WhosOnScreen })),
);
// #1666 — the Inbox's two stacked screens: the append-only archive
// (§5.2.4) and the per-type × channel preference matrix (§7 rule 5/6),
// which is where every transactional email's footer link now lands.
const InboxArchiveScreen = lazy(() =>
  import("./memberapp/inbox/ArchiveScreen").then((m) => ({ default: m.ArchiveScreen })),
);
const NotificationPrefsScreen = lazy(() =>
  import("./memberapp/inbox/NotificationPrefsScreen").then((m) => ({
    default: m.NotificationPrefsScreen,
  })),
);
const MyNumbersScreen = lazy(() =>
  import("./memberapp/screens").then((m) => ({ default: m.MyNumbersScreen })),
);
const ChangesScreen = lazy(() =>
  import("./memberapp/screens").then((m) => ({ default: m.ChangesScreen })),
);
const PrivacyScreen = lazy(() =>
  import("./memberapp/screens").then((m) => ({ default: m.PrivacyScreen })),
);
const TelegramScreen = lazy(() =>
  import("./memberapp/telegram/TelegramScreen").then((m) => ({ default: m.TelegramScreen })),
);
// #2077 — Message your lead (Profile sheet → `/me/message-lead`).
// #2945 slice 4 — the Team tab for every role on a roster team (a lead's
// Team home, a member's Team today) and the `/me` index that
// opens a member on Team today.
const TeamTabScreen = lazy(() =>
  import("./memberapp/team/TeamTabScreen").then((m) => ({ default: m.TeamTabScreen })),
);
const MemberIndexScreen = lazy(() =>
  import("./memberapp/team/MemberIndexScreen").then((m) => ({ default: m.MemberIndexScreen })),
);
const PeopleScreen = lazy(() =>
  import("./memberapp/team/PeopleScreen").then((m) => ({ default: m.PeopleScreen })),
);
// #2071 — the lead's Roster by day (lead phone app B2).
const RosterByDayScreen = lazy(() =>
  import("./memberapp/team/RosterByDayScreen").then((m) => ({ default: m.RosterByDayScreen })),
);
// #3064 — the lead's phone Stations (Team home's Stations door).
const StationsScreen = lazy(() =>
  import("./memberapp/team/StationsScreen").then((m) => ({ default: m.StationsScreen })),
);
// #2071 B3 — the lead's phone Review (Team home's leave + swap rows).
const TeamReviewScreen = lazy(() =>
  import("./memberapp/team/TeamReviewScreen").then((m) => ({ default: m.TeamReviewScreen })),
);
const MessageLeadScreen = lazy(() =>
  import("./memberapp/MessageLeadScreen").then((m) => ({ default: m.MessageLeadScreen })),
);
const IcsFeedScreen = lazy(() =>
  import("./memberapp/screens").then((m) => ({ default: m.IcsFeedScreen })),
);
const InFramePage = lazy(() =>
  import("./memberapp/screens").then((m) => ({ default: m.InFramePage })),
);
// #1682 — the member app's own not-found screen: the AppShell `*`
// catch-all is a desktop surface, so `/me/*` misses render this
// in-frame leaf instead (frame-integrity contract, design D1).
const MemberAppNotFound = lazy(() =>
  import("./memberapp/screens").then((m) => ({ default: m.MemberAppNotFound })),
);
// #1682 — the per-cycle submission form + per-shift feedback surface
// re-homed IN-FRAME. `SubmitPage` reads `:cycleId` from the path
// (issue 018 source-of-truth fix), so the member route mounts the
// same component the desktop `/cycles/:cycleId/submit` route serves.
const MemberSubmitPage = lazy(() => import("./components/SubmitPage"));
const MeFeedback = lazy(() =>
  import("./routes/me/MeFeedback").then((m) => ({ default: m.MeFeedback })),
);
// #1667 — the Requests ledger's two stacked screens (design §5.5.2):
// the three-step new-request flow and the per-request lifecycle detail.
const NewRequestFlow = lazy(() =>
  import("./memberapp/requests/NewRequestFlow").then((m) => ({ default: m.NewRequestFlow })),
);
const RequestDetailScreen = lazy(() =>
  import("./memberapp/requests/RequestDetailScreen").then((m) => ({
    default: m.RequestDetailScreen,
  })),
);
const MeExport = lazy(() => import("./routes/me/MeExport").then((m) => ({ default: m.MeExport })));
// #2181 — the member's own Fixed shifts list (every date window, from today
// on) + the redirect from the old per-window `/me/cycles/:id/propose`.
const FixedShiftsScreen = lazy(() =>
  import("./memberapp/fixedShifts/FixedShiftsScreen").then((m) => ({
    default: m.FixedShiftsScreen,
  })),
);
const FixedShiftsProposeRedirect = lazy(() =>
  import("./memberapp/fixedShifts/FixedShiftsRedirect").then((m) => ({
    default: m.FixedShiftsProposeRedirect,
  })),
);
const MeHours = lazy(() => import("./routes/me/MeHours").then((m) => ({ default: m.MeHours })));
const MeStatistics = lazy(() =>
  import("./routes/me/MeStatistics").then((m) => ({ default: m.MeStatistics })),
);
const MeSwaps = lazy(() => import("./routes/me/MeSwaps").then((m) => ({ default: m.MeSwaps })));
// #2945 — the Week sheet: the team's roster in the department's sheet shape.
const WeekSheetScreen = lazy(() =>
  import("./memberapp/sheet/WeekSheetScreen").then((m) => ({ default: m.WeekSheetScreen })),
);
// #1958 (1D of #1950) — the member's team roster is a date window
// (`/me/roster?from&to`); the old per-window URL redirects onto it.
const MemberRosterScreen = lazy(() =>
  import("./memberapp/calendar/MemberRosterScreen").then((m) => ({
    default: m.MemberRosterScreen,
  })),
);
const MeCycleRosterRedirect = lazy(() =>
  import("./memberapp/MeCycleRosterRedirect").then((m) => ({
    default: m.MeCycleRosterRedirect,
  })),
);
const AdminPage = lazy(() =>
  import("./routes/settings/AdminPage").then((m) => ({ default: m.AdminPage })),
);
const ProductInvitesPage = lazy(() =>
  import("./routes/admin/ProductInvitesPage").then((m) => ({ default: m.ProductInvitesPage })),
);
const UsagePage = lazy(() =>
  import("./routes/settings/UsagePage").then((m) => ({ default: m.UsagePage })),
);
const ProfilePage = lazy(() =>
  import("./routes/settings/ProfilePage").then((m) => ({ default: m.ProfilePage })),
);
// #2071 — connect Telegram from the web (the chat's link lands here).
const ConnectTelegramPage = lazy(() =>
  import("./routes/ConnectTelegramPage").then((m) => ({ default: m.ConnectTelegramPage })),
);
// #2071 S3 — Add a login to a Telegram-only account (`/add-login?t=`).
const AddLoginPage = lazy(() =>
  import("./routes/AddLoginPage").then((m) => ({ default: m.AddLoginPage })),
);
// #112 — the on-call types page was retired (on-call is a per-station
// condition now); runtime users hitting /setup/oncall are redirected to
// /setup/stations. The OnCallTypesPage component + its tests were removed
// as dead code in #911.
const AxesPage = lazy(() =>
  import("./routes/setup/AxesPage").then((m) => ({ default: m.AxesPage })),
);
// CC3 #1694 — team attribute-catalogue admin (Setup → Attributes).
const AttributesPage = lazy(() =>
  import("./routes/setup/AttributesPage").then((m) => ({ default: m.AttributesPage })),
);
// #1582 slice 3 — the R-103 Components register is the default
// components surface (classic at ?classic=1).
const ComponentsRegisterRoute = lazy(() =>
  import("./routes/setup/ComponentsRegisterPage").then((m) => ({
    default: m.ComponentsRegisterRoute,
  })),
);
// Issue #503 / #504 — wave-8C.1 partial-solve relaxation setup page.
const RelaxSettingsPage = lazy(() =>
  import("./routes/setup/RelaxSettingsPage").then((m) => ({ default: m.RelaxSettingsPage })),
);
const RetentionPage = lazy(() =>
  import("./routes/setup/RetentionPage").then((m) => ({ default: m.RetentionPage })),
);
// Roster Setup Coach companion — import a generated setup file.
const ImportSetupPage = lazy(() =>
  import("./routes/setup/ImportSetupPage").then((m) => ({ default: m.ImportSetupPage })),
);
// Issue #1873 — assisted workbook imports (leave projections, on-call rosters).
const WorkbookImportsPage = lazy(() =>
  import("./routes/setup/WorkbookImportsPage").then((m) => ({ default: m.WorkbookImportsPage })),
);
// #1883 — Setup → Postings: where everyone sits, by date.
const PostingsPage = lazy(() =>
  import("./routes/setup/PostingsPage").then((m) => ({ default: m.PostingsPage })),
);
// Native (React) Roster Setup Coach — full-screen guided onboarding.
const GuidedSetupPage = lazy(() =>
  import("./routes/setup/guided/GuidedSetupPage").then((m) => ({ default: m.GuidedSetupPage })),
);
// #1582 slice 3 — the R-102 "Rules & fairness" sheet is the default
// rules surface (classic pages at ?classic=1 during the preview
// window); /setup/balance folds into it.
const RulesFairnessRoute = lazy(() =>
  import("./routes/setup/RulesFairnessPage").then((m) => ({ default: m.RulesFairnessRoute })),
);
const SolverBalanceRoute = lazy(() =>
  import("./routes/setup/RulesFairnessPage").then((m) => ({ default: m.SolverBalanceRoute })),
);
// #112 — SessionsPage no longer routed (Sessions/Tiers as cascadables
// in Stations #110; Templates moved to Stations cascadable;
// FeedbackConfig moved to SolverBalance → now the R-102 sheet).
// Issue #317 — lead station-group priority setup (Feature E §9.13).
// #1630 — re-homed as the R-104 "Solve groups" register inside the
// Stations Field Desk; this route wrapper redirects (classic page at
// `?classic=1` for the preview window).
const StationGroupsRoute = lazy(() =>
  import("./routes/setup/stations/SolveGroupsSheet").then((m) => ({
    default: m.StationGroupsRoute,
  })),
);
const StationsPage = lazy(() =>
  import("./routes/setup/StationsPage").then((m) => ({ default: m.StationsPage })),
);
// Issue #292 — per-station workload-weights setup page (Feature C).
const WorkloadWeightsPage = lazy(() =>
  import("./routes/setup/WorkloadWeightsPage").then((m) => ({ default: m.WorkloadWeightsPage })),
);

// Issue #1426 — recurring fixed allocations setup page.
const RecurringAllocationsPage = lazy(() =>
  import("./routes/setup/RecurringAllocationsPage").then((m) => ({
    default: m.RecurringAllocationsPage,
  })),
);

export function App() {
  // #2074 — the Telegram Mini App (`/tg`) never loads Clerk: Telegram signs
  // the member in (`AuthContext` → `TelegramAuthProvider`).
  // #2824 - nor does the demo phone's frame (`/demo-phone`): it signs in as a
  // staff member of the demo through the visitor's own demo token.
  if (hasClerk && clerkPublishableKey && !onMiniAppPath() && !inDemoPhoneFrame()) {
    // `signInUrl` / `signUpUrl` tell Clerk where OUR pages are (custom
    // sign-in, 3 Oct 2026). Without them Clerk's fallbacks — an OAuth
    // callback's leftover step, a session task — point at Clerk's hosted
    // Account Portal instead of `/sign-in` / `/sign-up`.
    // #3005 — with several accounts signed in, Clerk ending the current one
    // opens Choose an account (multi-session mode only).
    return (
      <ClerkProvider
        publishableKey={clerkPublishableKey}
        signInUrl="/sign-in"
        signUpUrl="/sign-up"
        afterMultiSessionSingleSignOutUrl={CHOOSE_ACCOUNT_PATH}
      >
        <AppRoutes />
      </ClerkProvider>
    );
  }
  return <AppRoutes />;
}

function onMiniAppPath(): boolean {
  return typeof window !== "undefined" && isMiniAppPath(window.location.pathname);
}

function AppRoutes() {
  // #2074 — inside Telegram the whole router runs under `/tg`, so the member
  // app's own paths (`/me/inbox`, …) are the Mini App's and every in-app link
  // stays inside it.
  const miniApp = onMiniAppPath();
  // #2824 - the demo phone runs the same member routes under `/demo-phone`.
  const phoneFrame = inDemoPhoneFrame();
  return (
    <BrowserRouter basename={miniApp ? "/tg" : phoneFrame ? DEMO_PHONE_BASENAME : "/"}>
      {/* #2052 — a new page opens at the top (search-only changes keep place). */}
      <ScrollToTopOnRouteChange />
      {/* #2919 — "Tarii has an update." strip + apply on the next page change. */}
      <AppUpdateStrip />
      <AuthProvider>
        {/* #2919 — a change of account clears the account caches. */}
        <AccountCacheGuard />
        {/* #3005 — other tabs follow a switch of account (D2 a). */}
        <AccountSwitchNotice />
        <BreadcrumbProvider>
          <TooltipProvider>
            {/* WS6 OPT-2 (refs #823) — one Suspense boundary around the
                whole route table catches every lazily-loaded route body
                (see the `lazy(...)` block above). The fallback mirrors
                the `PageSkeleton` shape used in `routes/CycleRoutes.tsx`
                so a route's first visit shows a calm skeleton, not a
                blank flash, while its chunk downloads. Eager routes
                (landing/auth/onboarding) render synchronously and never
                hit the fallback. */}
            <Suspense fallback={<RouteSuspenseFallback />}>
              {miniApp ? (
                <TelegramMiniApp>{memberAppRoutes()}</TelegramMiniApp>
              ) : phoneFrame ? (
                <DemoPhoneFrame>{memberAppRoutes()}</DemoPhoneFrame>
              ) : (
                <Routes>
                  {/* Unauthenticated surfaces. */}
                  <Route path="/sign-in/*" element={<SignInGate />} />
                  <Route path="/sign-up/*" element={<SignUpGate />} />
                  {/* Custom sign-in (3 Oct 2026) — Google returns here. */}
                  <Route path="/sso-callback" element={<SsoCallbackGate />} />
                  {/* #1753 — `/demo` is the ephemeral surface (one minted
                  per-visitor team, one 15 s solve, 2 h TTL). It falls
                  back to the persona picker when the mint endpoint
                  isn't live yet (SPA deploys ahead of the backend).
                  The picker itself stays at `/demo/scenarios` for
                  sales demos + the shared tarii-time.com logins. Both
                  are under `/demo*` so `isDemoPath` keeps gating the
                  dev shim correctly. */}
                  {/* #2985 — the industry and team lists stay open; the access
                  key prompt sits on the team page's start action only. */}
                  <Route path="/demo" element={<DemoStartPage />} />
                  <Route path="/demo/scenarios" element={<DemoPage />} />
                  <Route path="/demo/industry/:industry" element={<DemoIndustryPage />} />
                  <Route
                    path="/demo/:scenario"
                    element={
                      <DemoAccessGate>
                        <DemoScenarioPage />
                      </DemoAccessGate>
                    }
                  />
                  {/* #1719 round 6 (Timble structure) — public per-product
                  detail pages the landing's 02 rows and footer link to.
                  Unknown slugs bounce home inside the component. */}
                  <Route path="/features/:slug" element={<FeaturePage />} />
                  <Route path="/the-problem-with-rostering" element={<ProblemPage />} />
                  {/* 15 Oct 2026 — the tab was renamed. The old address redirects. */}
                  <Route
                    path="/problem-and-solution"
                    element={<Navigate to="/the-problem-with-rostering" replace />}
                  />
                  {/* 2 Oct 2026 — How it works replaces the products page:
                  Tarii is one scheduling process, not five products.
                  The old /products address redirects. */}
                  <Route path="/how-it-works" element={<HowItWorksPage />} />
                  <Route path="/products" element={<Navigate to="/how-it-works" replace />} />
                  {/* #2098 — public privacy policy + terms. The production
                  Google OAuth consent screen links /privacy and /tos, so
                  both are unauthenticated; /tos is an alias for /terms. */}
                  <Route path="/privacy" element={<PrivacyPage />} />
                  <Route path="/terms" element={<TermsPage />} />
                  <Route path="/tos" element={<Navigate to="/terms" replace />} />
                  {/* #1719 round 7 (full marketing site) — public
                  per-industry use-case pages; same contract as the
                  product pages, unknown slugs bounce home. */}
                  <Route
                    path="/industries/:slug"
                    element={<Navigate to="/how-it-works" replace />}
                  />
                  <Route path="/pricing" element={<PricingPage />} />
                  <Route path="/data-handling" element={<DataHandlingPage />} />
                  {/* Design-system styleguide (Claude design pipeline PoC).
                  Public + unauthenticated; renders ui/* primitives + tokens
                  for reskin review + visual-regression screenshots. */}
                  <Route path="/__styleguide" element={<StyleguideRoute />} />
                  {/* Tom F1 (`docs/audits/ux-log-tom-member-2026-05-01.md`)
                  — `/invite/:token` is the unauthenticated PREVIEW
                  surface (calm context: team + inviter + role + the
                  three-circles privacy strip). Members hit Clerk only
                  after reading what they're joining. The post-auth
                  membership-creation lives at `/invite/:token/accept`,
                  reached via `consumePendingInviteRedirect()` after
                  Clerk auth completes. */}
                  <Route path="/invite/:token" element={<InvitePreviewPage />} />
                  {/* #1720 round 4 — the get-started chooser is PUBLIC:
                  the landing's Sign up CTA leads here first, so the
                  lead-vs-member branch is asked before Clerk. The page
                  is auth-aware — logged-out doors stash the intent and
                  continue to `/sign-up`; a zero-team authenticated
                  account routed here by `<HomeEntryGate/>` gets doors
                  straight to `/onboarding` / `/join`. */}
                  <Route path="/start" element={<StartPage />} />
                  {/* #774 — `<AccountDebug/>` hardcodes a `Bearer dev:<uuid>`
                  impersonation header, so the `/dev` route must NOT ship
                  in the prod SPA build. `import.meta.env.DEV` is true only
                  under `vite dev`; the prod `vite build` tree-shakes this
                  branch out, dropping the live impersonation probe. */}
                  {import.meta.env.DEV && <Route path="/dev" element={<AccountDebug />} />}

                  {/* Authenticated app. RequireAuthOrLanding gates, AppShell
                  paints. The gate renders the calm marketing
                  <LandingPage/> for unauthenticated visitors on `/`
                  (issue 002 — `docs/issues/002-incognito-auto-shims-demo-persona.md`)
                  and bounces unauthenticated visitors on every other
                  path to `/sign-in`. Authenticated visitors fall
                  through to the existing AppShell + outlet tree. */}
                  <Route element={<RequireAuthOrLanding />}>
                    <Route path="/onboarding" element={<OnboardingPage />} />
                    {/* #1720 — `/join` sits HERE, a sibling of
                    `/onboarding`: inside the auth gate (it names the
                    signed-in account's email and leads into membership
                    creation) and outside the AppShell (a first-run
                    decision surface, not a chrome surface). It sits
                    above `<RequireDisplayName/>` for the same reason: a
                    brand-new Clerk account has no display name, and
                    bouncing it through the profile detour before it has
                    told us whether it is joining or creating puts the
                    least interesting question first. Its sibling
                    chooser `/start` moved OUTSIDE the gate in round 4 —
                    the landing's Sign up CTA reaches it pre-auth.
                    `<HomeEntryGate/>` below is what routes people to
                    both post-auth. */}
                    <Route path="/join" element={<JoinPage />} />
                    {/* #2071 S3 — `/add-login?t=`: the Mini App's Add a
                    login opens here in the phone's browser. Inside the auth
                    gate (the person signs in with the login they are
                    adding; `?t=` survives it, `lib/returnTo`) and outside
                    the AppShell and the display-name / welcome gates: the
                    account that sign-in just made is empty, and this page
                    folds it into the Telegram account. */}
                    <Route path="/add-login" element={<AddLoginPage />} />
                    {/* Tom F1 — post-auth invite acceptance. The preview at
                    `/invite/:token` (unauth) is the public-facing
                    surface; once the user signs up + Clerk lands them
                    via `consumePendingInviteRedirect()`, they end up
                    here and the membership is minted. Mounted outside
                    AppShell because the page is a centred-Logo +
                    Card transition surface, not a chrome surface. */}
                    <Route path="/invite/:token/accept" element={<InviteAcceptPage />} />
                    {/* Tom (member) UX log F2 P0 — first-time-member welcome
                    interstitial. Sits OUTSIDE AppShell (matches the
                    OnboardingPage pattern) so the focal three-circles
                    privacy strip lands without competing chrome. The
                    `<MemberWelcomeGate/>` wrapper redirects every
                    other authenticated route here for a wary worker
                    on first entry. */}
                    <Route path="/me/welcome" element={<MeWelcome />} />
                    {/* Issue 010 / Brief 37 — first-run display-name gate.
                    Sits between RequireAuthOrLanding and MemberWelcomeGate
                    so a new account whose JWT didn't carry name claims
                    (cascade step 6 → `User <id-prefix>`) is bounced to
                    `/settings/profile?onboarding=1` before reaching any
                    AppShell chrome. Account-scoped (not role-scoped):
                    leads + members both hit the cascade fallback. The
                    `/me/welcome` and `/sign-in` routes stay outside the
                    gate so first-time members + unauthed visitors are
                    unaffected. The gate short-circuits via <Outlet/>
                    when the user already has a real name OR has
                    dismissed the prompt OR is already on the profile
                    page — see the bypass-condition list in
                    `RequireDisplayName.tsx`. */}
                    <Route element={<RequireDisplayName />}>
                      <Route element={<MemberWelcomeGate />}>
                        {/* #1682 — member-first landing. Sits BELOW the
                        welcome gate (first-run ordering intact: an
                        undismissed member still sees /me/welcome first)
                        and ABOVE AppShell (a member-only account never
                        paints desktop chrome on `/`). Wraps only the
                        AppShell block — the member app's own routes
                        need no landing hop. */}
                        {/* #1720 — `<HomeEntryGate/>` composes the zero-team
                        create-or-join branch AHEAD of the member-landing
                        branch, both of which answer "where does `/` go
                        for this account?". Composed inside one route
                        element rather than nested as a second wrapper
                        route because they are one decision, evaluated in
                        one order, on one path. */}
                        <Route element={<HomeEntryGate />}>
                          <Route element={<AppShell />}>
                            <Route index element={<HomePage />} />
                            <Route path="teams/:teamId" element={<TeamPage />} />
                            {/* Issue #820 (R2.3) — `/teams/:teamId/members`
                          now renders the full members-management page
                          (the wide attribute table). It is ADDITIVE to
                          the team-home members sidebar, which stays the
                          default surface. Pre-#820 this URL was a legacy
                          alias (`<TeamMembersAliasRedirect/>`, still
                          exported for the #379 redirect test) that
                          stripped `?tab=members` and bounced to team
                          home. The sidebar's "Manage members" link +
                          the AppShell "Members" nav entry both target
                          this route now. */}
                            {/* #2903 — the Team page: Stations (default), Access, Activity. */}
                            <Route path="teams/:teamId/team" element={<TeamOversightPage />}>
                              <Route index element={<TeamPageIndex />} />
                              <Route path="stations" element={<StationsTab />} />
                              <Route path="access" element={<AccessTab />} />
                              <Route path="activity" element={<ActivityTab />} />
                            </Route>
                            <Route path="teams/:teamId/members" element={<PeoplePage />} />
                            {/* #1282 — manpower planning dashboard (headcount
                          heatmap + joins/leaves/planned-change markers)
                          and the per-person profile node (effective-dated
                          FTE / tier timeline). Lead-only surfaces; the
                          profile node is also readable by the member it
                          belongs to (peer reads 404 server-side). Both
                          component-gated on `manpower_planning`. */}
                            <Route
                              path="teams/:teamId/manpower"
                              element={<ManpowerToPeopleRedirect />}
                            />
                            <Route
                              path="teams/:teamId/members/:membershipId/profile"
                              element={<MemberProfilePage />}
                            />
                            {/* #2903 — the old Team activity address opens the Activity tab. */}
                            <Route path="teams/:teamId/audit" element={<TeamAuditRedirect />} />
                            {/* #2834 — the Insights link at the foot of the team home. */}
                            <Route path="teams/:teamId/insights" element={<TeamInsightsPage />} />
                            {/* Issue #264 — team-outcomes dashboard. Composes
                          per-axis fairness, leave utilisation, standby
                          (placeholder at v0.1), and compliance evidence
                          into one read-only lead-facing surface. Direct
                          URL only at v0.1; component-gated by
                          `team_outcomes_dashboard` (default-on across
                          all plans). */}
                            <Route path="teams/:teamId/outcomes" element={<TeamOutcomesPage />} />
                            {/* Issue #1070 (WS-A) → #1959 — the team's Solves
                          page: the solve history and the date line (every
                          date's solve status, drag-select a span). #2030 —
                          its address is `/solves`; the old `/timeline`
                          redirects there so old links hold. */}
                            <Route path="teams/:teamId/solves" element={<TeamTimelinePage />} />
                            <Route path="teams/:teamId/timeline" element={<TeamSolvesRedirect />} />
                            {/* #1956 — the old range view and stacked-weeks
                          page redirect to the roster page with the same
                          dates. */}
                            <Route
                              path="teams/:teamId/schedule/range"
                              element={<TeamRosterRangeRedirect />}
                            />
                            {/* #1956 — Team → Roster IS the roster: a window
                          on the team's date line (`?from&to&preset`). */}
                            <Route path="teams/:teamId/roster" element={<TeamRosterPage />} />
                            {/* #2675, #2797 — the old address of the station view.
                          It redirects to the roster page in Floor mode (the
                          fourth Rows choice of Layout ▾). */}
                            <Route path="teams/:teamId/floor" element={<TeamFloorRedirect />} />
                            <Route
                              path="teams/:teamId/roster/weeks"
                              element={<TeamRosterRangeRedirect />}
                            />
                            {/* Issue #162 — lead-side hire-by Forecast page.
                          Composes existing F1 + F2 forecast helpers
                          with a deterministic rules-based projection
                          (sustainable-through + hire-by + suggested
                          role). Component-gated by `forecast`. */}
                            <Route path="teams/:teamId/forecast" element={<TeamForecastPage />} />
                            {/* Issue #159 — live team-mood deep-dive page.
                          Lead-only deep dive that pairs with the
                          `<LiveMoodSection/>` pill in the persistent
                          right panel. Component-gated by `live_mood`
                          at the page-body level so a team without the
                          component sees no chrome. Direct URL only at
                          v0.1; the panel pill is the in-app
                          affordance. */}
                            <Route path="teams/:teamId/insights/mood" element={<TeamMoodPage />} />
                            {/* Issue #163 — retrospective analysis. Longitudinal
                          per-axis Gini trends + flag-driver categories +
                          schedule-shape correlations + override-with-reason
                          corpus across the team's last 12 cycles. Lead-only
                          read-only surface; component-gated by
                          `retrospective`. */}
                            <Route
                              path="teams/:teamId/retrospective"
                              element={<TeamRetrospectivePage />}
                            />
                            {/* Issue #161 — pattern-triggered workflows config.
                          Lead-only `/teams/:tid/workflows` config page +
                          panel ticker (`<WorkflowTriggerSection/>`) +
                          worker-side disclosure card on `/me/insights`.
                          Component-gated by `workflows` (depends on
                          `override_reasoning` + `live_mood` per registry). */}
                            <Route path="teams/:teamId/workflows" element={<TeamWorkflowsPage />} />
                            {/* Native Roster Setup Coach — a focused,
                          full-screen guided onboarding experience.
                          Deliberately OUTSIDE <TeamSetupLayout/> (no
                          narrow sub-nav column) so the guide dominates
                          the viewport; the Setup → Guided setup tab is a
                          launch card that links here. Ungated; the import
                          endpoint enforces the lead gate. */}
                            <Route
                              path="teams/:teamId/guided-setup"
                              element={<GuidedSetupPage />}
                            />
                            <Route path="teams/:teamId/setup" element={<TeamSetupLayout />}>
                              {/* Issue #112 — sessions/oncall tabs retired
                            (Sessions/Tiers as cascadables in Stations
                            #110; on-call as a per-station condition).
                            The retired routes redirect to Stations
                            instead of 404'ing on deep-linked URLs from
                            emails / docs. Bare /setup also lands on
                            Stations now since it's the new default. */}
                              <Route index element={<Navigate to="stations" replace />} />
                              <Route
                                path="sessions"
                                element={<Navigate to="../stations" replace />}
                              />
                              <Route path="stations" element={<StationsPage />} />
                              <Route
                                path="oncall"
                                element={<Navigate to="../stations" replace />}
                              />
                              {/* CC3 #1694 — attribute catalogue admin. Un-breaks
                            the two shipped links that pointed here before the
                            page existed (the Stations drawer's composition-floor
                            hint + the member card's empty-attributes hint).
                            Ungated: the catalogue predates the
                            `attribute_conditions` component. */}
                              <Route path="attributes" element={<AttributesPage />} />
                              <Route path="rules" element={<RulesFairnessRoute />} />
                              <Route path="balance" element={<SolverBalanceRoute />} />
                              <Route path="components" element={<ComponentsRegisterRoute />} />
                              {/* Roster Setup Coach companion — import a
                            generated setup file (rules + member
                            placeholders). Ungated; the endpoint enforces
                            the lead gate. */}
                              <Route path="import" element={<ImportSetupPage />} />
                              <Route path="workbooks" element={<WorkbookImportsPage />} />
                              <Route path="postings" element={<PostingsPage />} />
                              <Route path="retention" element={<RetentionPage />} />
                              {/* Issue #164 — custom fairness axes admin. */}
                              <Route path="axes" element={<AxesPage />} />
                              {/* Issue #292 — Feature C per-station workload weights.
                            Component-gated by `workload_axis` at the page-body
                            level; the route is always mounted so deep links
                            don't 404 (the body shows the off-card when the
                            component is off). */}
                              <Route path="workload-weights" element={<WorkloadWeightsPage />} />
                              {/* Issue #317 → #1630 — station grouping is
                            the R-104 "Solve groups" register inside the
                            Stations Field Desk. This route stays mounted
                            so deep links never 404: it redirects to
                            `/setup/stations?sheet=solve-groups` (classic
                            page survives at `?classic=1` for the preview
                            window). */}
                              <Route path="station-groups" element={<StationGroupsRoute />} />
                              {/* Issue #1426 — recurring fixed allocations setup.
                            Gated by `recurring_fixed_allocations` at the body
                            level; route always mounted so deep links don't
                            404. */}
                              <Route
                                path="recurring-allocations"
                                element={<RecurringAllocationsPage />}
                              />
                              {/* Issue #503 / #504 — wave-8C.1 partial-solve
                            relaxation setup. Component-gated by
                            `partial_solve` at the page-body level; the
                            route is always mounted so deep links don't
                            404 (the body shows the off-card when the
                            component is off). */}
                              <Route path="relax-settings" element={<RelaxSettingsPage />} />
                              {/* #2030 — an unknown Setup address stays in
                            the Setup layout (section list + Back to Setup)
                            instead of falling to the shell's bare 404. */}
                              <Route path="*" element={<SetupNotFound />} />
                            </Route>
                            {/* Issue #753 — task-mode routes (Phase 0, dark).
                          A tasks team (`team_kind='tasks'`) mounts its
                          app under `/teams/:teamId/map/...`. Scheduling
                          teams never reach these routes; tasks teams
                          never mount cycle/station/solver routes.
                          Dark: no prod team has `team_kind='tasks'`
                          while `TARII_TASK_MODE` gate is off. */}
                            {/* #1043 PR-D (founder F5, 2026-05-26) — the default
                          landing FLIPPED to Home. The bare `/map` is now the
                          overview Home dashboard; the Taskmap canvas+outline
                          editor moved to `/map/board`. */}
                            <Route path="teams/:teamId/map" element={<MapHomePageDashboard />} />
                            <Route path="teams/:teamId/map/board" element={<MapHomePage />} />
                            {/* `/map/home` is a stale-bookmark redirect to the bare
                          `/map` (now Home). Uses an absolute redirect built from
                          `:teamId` (a relative `..` would strip the whole flat
                          route back to `/`). */}
                            <Route path="teams/:teamId/map/home" element={<MapHomeRedirect />} />
                            <Route path="teams/:teamId/map/members" element={<MapMembersPage />} />
                            <Route
                              path="teams/:teamId/map/settings"
                              element={<MapSettingsPage />}
                            />
                            {/* #1043 — the standalone `/map/history` route is
                          RETIRED. History lives on the Home dashboard as a
                          project-wide activity feed beside the task list
                          (`<ProjectActivityFeed/>` in `<HomeDashboard/>`);
                          per-node history is in the node drawer's History tab.
                          No route needed. */}
                            {/* IA Phase 1a — cycle 5-tab sub-routes. Bare
                      `/cycles/:id` redirects to `/cycles/:id/roster`
                      (default landing tab). */}
                            <Route path="cycles/:cycleId" element={<CycleIndexRoute />} />
                            <Route path="cycles/:cycleId/roster" element={<CyclePageRoute />} />
                            <Route
                              path="cycles/:cycleId/submissions"
                              element={<CyclePageRoute />}
                            />
                            {/* Issue 013 §13.B — Leaves promoted to a top-level
                        cycle tab. Visible to leads (team-wide spans) +
                        members (own-only). */}
                            <Route path="cycles/:cycleId/leaves" element={<CyclePageRoute />} />
                            {/* Issue #91 — per-shift feedback tab. Issue #108
                          fix-up: PR #93 added the <TabsTrigger> on
                          <CyclePage> but missed the Route entry, so
                          clicking Feedback navigated to a path with no
                          matching route and rendered blank. */}
                            <Route path="cycles/:cycleId/feedback" element={<CyclePageRoute />} />
                            <Route path="cycles/:cycleId/solve" element={<CyclePageRoute />} />
                            <Route path="cycles/:cycleId/demand" element={<CyclePageRoute />} />
                            <Route path="cycles/:cycleId/fairness" element={<CyclePageRoute />} />
                            <Route path="cycles/:cycleId/hours" element={<CyclePageRoute />} />
                            {/* Issue #306 §9.6 — Feature B lead swap oversight
                          board. Lead-only; in-tab gated by `swap_requests`
                          so a team with the component off sees no tab in
                          the cycle nav. */}
                            <Route path="cycles/:cycleId/swaps" element={<CyclePageRoute />} />
                            <Route path="cycles/:cycleId/submit" element={<SubmitRoute />} />
                            <Route path="cycles/:cycleId/blocks" element={<BlocksEditorRoute />} />
                            <Route path="cycle" element={<LegacyCycleDashboardRoute />} />
                            <Route path="dashboard" element={<LegacyCycleDashboardRoute />} />
                            <Route path="submit" element={<SubmitRoute />} />
                            {/* #1665 — every `/me/*` surface now lives in the
                          member phone app (the `<MemberAppLayout/>` route
                          block below, a SIBLING of this AppShell block so
                          the frame renders without desktop chrome). The
                          §4.2 nothing-vanishes contract: routes re-homed,
                          none removed, zero 404s. */}
                            <Route path="insights/capacity" element={<CapacityRoute />} />
                            <Route path="capacity" element={<CapacityRoute />} />
                            {/* Brief 29 / issue 001 — user-editable display-name
                      path. Account-scoped (NOT team-scoped) so the
                      route works regardless of the active workspace.
                      Reachable via direct URL only at v0.1; AppShell
                      dropdown link integration is a separate decision
                      (sibling brief owns the user-pill dropdown). */}
                            <Route path="settings/profile" element={<ProfilePage />} />
                            {/* #2071 — connect Telegram: with `?t=` it claims
                            the chat that handed out the link (the chat's
                            Connect Tarii button, the Mini App's Connect this
                            chat); without, the status screen — how a lead
                            on a desktop connects (account menu → Telegram).
                            The address survives sign-in (`lib/returnTo`). */}
                            <Route path="connect/telegram" element={<ConnectTelegramPage />} />
                            {/* Issue #31 — admin / developer-settings page.
                      Backend gate (`require_admin` in
                      `backend/src/api/admin_gate.py`) returns 404 to
                      non-admin callers, so AdminPage probes via a
                      `GET /api/admin/accounts` and renders a calm
                      "page not found" state for non-operators. The
                      surface stays invisible to non-admins by design. */}
                            <Route path="settings/admin" element={<AdminPage />} />
                            {/* #2652 — product invite links. Same 404-as-not-found
                            posture: the API gate hides it from non-admins. */}
                            <Route path="admin/product-invites" element={<ProductInvitesPage />} />
                            {/* Issue #1466 — internal usage + cost dashboard.
                          Same posture as `settings/admin` above: the
                          backend `require_admin` gate 404s non-admins and
                          the page renders that as a calm "not found", so
                          the surface stays invisible to non-operators. Sits
                          at `settings/usage` (not the spec's `/admin/usage`)
                          because `routes/settings/` is where this codebase's
                          operator pages actually live. */}
                            <Route path="settings/usage" element={<UsagePage />} />
                            {/* IA Phase 1a — top-level `/solve` removed from
                      AppShell nav. The route stays as a redirect for
                      bookmarks + email-in-flight links. */}
                            <Route path="solve" element={<LegacySolveRedirect />} />
                            <Route path="modules/:moduleName" element={<ModulePage />} />
                            {/* #2098 — the old in-app stubs now point at the
                            public pages, so there is one copy of each. */}
                            <Route
                              path="legal/privacy"
                              element={<Navigate to="/privacy" replace />}
                            />
                            <Route path="legal/terms" element={<Navigate to="/terms" replace />} />
                            <Route path="*" element={<NotFound />} />
                          </Route>
                        </Route>
                        {/* #1665 — the member phone app (design §2/§3/D1):
                        one frame, three or four tabs (#2945: Team on a
                        roster team), stacked screens, sheets.
                        A SIBLING of the AppShell block (both inside
                        `<MemberWelcomeGate/>`) so the app owns its own
                        chrome. Every legacy `/me/*` URL resolves here —
                        tabs, redirects, or the legacy page re-homed
                        INSIDE the frame (§4.2 nothing-vanishes table;
                        zero 404s). `/me/welcome` stays outside as the
                        app's first-run (§5.6). */}
                        {memberAppRoutes()}
                      </Route>
                    </Route>
                  </Route>
                </Routes>
              )}
            </Suspense>
          </TooltipProvider>
        </BreadcrumbProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

/**
 * #1665 — the member phone app's routes (one frame, three tabs and #2945's
 * Team on a roster team, stacked screens). Mounted twice: under the web app's auth gates at `/me/*`, and
 * (#2074) as the Telegram Mini App's whole route table, where the router's
 * `basename="/tg"` puts the same paths under `/tg/me/*`.
 */
function memberAppRoutes() {
  return (
    <Route element={<MemberAppLayout />}>
      {/* #2945 — a member opens on Team today; a lead keeps the Calendar. */}
      <Route path="me" element={<MemberIndexScreen calendar={<MemberCalendarTab />} />} />
      <Route path="me/inbox" element={<MemberInboxTab />} />
      <Route path="me/inbox/archive" element={<InboxArchiveScreen />} />
      <Route path="me/notifications" element={<NotificationPrefsScreen />} />
      <Route path="me/telegram" element={<TelegramScreen />} />
      <Route path="me/tasks" element={<MemberTasksTab />} />
      {/* #2071 / #2945 — the Team tab: a lead's Team home, a member's
        Team today. #3063 — the Week sheet is its own tab (`/me/sheet`). */}
      <Route path="me/team" element={<TeamTabScreen />} />
      {/* #2071 B6 — People on the phone; `:membershipId` = their messages. */}
      <Route path="me/team/people/:membershipId?" element={<PeopleScreen />} />
      {/* #2071 B2 — the lead's Roster by day + the ladder. */}
      <Route path="me/team/roster" element={<RosterByDayScreen />} />
      {/* #2071 B3 — leave requests + held swaps to review. */}
      <Route path="me/team/review" element={<TeamReviewScreen />} />
      {/* #3064 — each station's fill now, in cover groups (`?d=` for a day). */}
      <Route path="me/team/stations" element={<StationsScreen />} />
      {/* §4.2 redirects — exported leafs, unit-testable
        in isolation (#379 pattern). */}
      <Route path="me/schedule" element={<MeScheduleAliasRedirect />} />
      {/* Calendar-owned stacked screens. #1958 — the
        team roster by date window. */}
      <Route path="me/roster" element={<MemberRosterScreen />} />
      <Route path="me/whos-on" element={<WhosOnScreen />} />
      {/* #2945 — the Week sheet (`?date=`), one day upright, the week sideways. */}
      <Route path="me/sheet" element={<WeekSheetScreen />} />
      <Route path="me/changes" element={<ChangesScreen />} />
      {/* Profile-sheet screens (§4.3): My numbers links
        out to the EXISTING mirror pages rendered
        in-frame (founder Q7 verdict — no native
        rebuild in P0; every S/H/I/C/F axis intact). */}
      <Route path="me/numbers" element={<MyNumbersScreen />} />
      <Route path="me/privacy" element={<PrivacyScreen />} />
      <Route path="me/feed" element={<IcsFeedScreen />} />
      {/* #2077 — one note to the lead (Circle 2). */}
      <Route path="me/message-lead" element={<MessageLeadScreen />} />
      <Route
        path="me/hours"
        element={
          <InFramePage>
            <MeHours />
          </InFramePage>
        }
      />
      <Route
        path="me/statistics"
        element={
          <InFramePage>
            <MeStatistics />
          </InFramePage>
        }
      />
      <Route
        path="me/insights"
        element={
          <InFramePage>
            <MeInsights />
          </InFramePage>
        }
      />
      <Route
        path="me/comply"
        element={
          <InFramePage>
            <MeComply />
          </InFramePage>
        }
      />
      <Route
        path="me/forecast"
        element={
          <InFramePage>
            <MeForecast />
          </InFramePage>
        }
      />
      <Route
        path="me/audit"
        element={
          <InFramePage>
            <MeAudit />
          </InFramePage>
        }
      />
      <Route
        path="me/export"
        element={
          <InFramePage>
            <MeExport />
          </InFramePage>
        }
      />
      {/* #1667 — `/me/leaves` retired: the Tasks tab
        IS the requests surface now (§5.5). The alias
        stays a redirect because decks, docs and the
        P0 tab linked it — zero 404s (§4.2). */}
      <Route path="me/leaves" element={<MeLeavesTasksRedirect />} />
      <Route path="me/requests/new" element={<NewRequestFlow />} />
      <Route path="me/requests/:leaveId" element={<RequestDetailScreen />} />
      <Route
        path="me/swaps"
        element={
          <InFramePage>
            <MeSwaps />
          </InFramePage>
        }
      />
      {/* #2181 — the member's own fixed shifts, across every date window.
        The old per-window screen's URL redirects here (zero 404s). */}
      <Route path="me/fixed-shifts" element={<FixedShiftsScreen />} />
      <Route path="me/cycles/:cycleId/propose" element={<FixedShiftsProposeRedirect />} />
      <Route path="me/cycles/:cycleId/roster" element={<MeCycleRosterRedirect />} />
      {/* #1682 — the two frame-escape destinations
        re-homed IN-FRAME: session preferences (the
        Tasks banner + Standing row used to point at
        the desktop `/cycles/:id/submit`) and the
        per-shift feedback form (the event sheet's
        "Leave feedback" used to open the desktop
        drawer over the desktop roster). The desktop
        routes stay mounted for leads. */}
      <Route
        path="me/cycles/:cycleId/submit"
        element={
          <InFramePage>
            <MemberSubmitPage />
          </InFramePage>
        }
      />
      <Route
        path="me/cycles/:cycleId/feedback"
        element={
          <InFramePage>
            <MeFeedback />
          </InFramePage>
        }
      />
      {/* #1682 — in-frame 404: any retired or mistyped
        `/me/*` URL renders the member app's own
        not-found screen INSIDE the frame. Without
        this, the AppShell `*` catch-all (a desktop
        surface) claimed these paths — a frame
        escape. */}
      <Route path="me/*" element={<MemberAppNotFound />} />
    </Route>
  );
}

/**
 * Issue #379 — `/teams/:teamId/members` → `/teams/:teamId?tab=members`.
 *
 * SUPERSEDED by #820 (R2.3): `/teams/:teamId/members` now renders the
 * full members-management page (`<MembersPage/>`), so this redirect is
 * no longer mounted on that route. The component is RETAINED (and still
 * exported) only for the #379 redirect unit test, which exercises it in
 * isolation. Safe to delete once that test is retired.
 */
export function TeamMembersAliasRedirect() {
  const { teamId } = useParams<{ teamId: string }>();
  if (!teamId) return <Navigate to="/" replace />;
  return <Navigate to={`/teams/${teamId}?tab=members`} replace />;
}

/**
 * #1645 — `/me/schedule` → `/me`.
 *
 * The two URLs rendered the SAME page, which split bookmarks and made
 * the member IA read as bigger than it is. The alias becomes a redirect
 * rather than a 404 because decks, docs, and the demo recording guide
 * link to `/me/schedule`. Mounted INSIDE `<MemberWelcomeGate/>` (like
 * every other `/me/*` route except `/me/welcome`), so an undismissed
 * first-time member hitting the alias still lands on the welcome
 * interstitial first — the gate's redirect wins before this one
 * renders, and after dismissal the alias resolves to `/me` with no
 * ping-pong. Since #1665, `/me` is the member app's Calendar tab — the
 * §4.2 table's "`/me/schedule` → Calendar" row rides this same leaf.
 */
export function MeScheduleAliasRedirect() {
  return <Navigate to="/me" replace />;
}

/**
 * #1667 (§4.2) — `/me/leaves` → the Tasks tab.
 *
 * The Requests ledger IS the tab now: the old submit form became the
 * `/me/requests/new` flow and the rows live on `/me/tasks`. The alias
 * becomes a redirect rather than a 404 because the P0 Tasks tab, the
 * calendar's absence sheet and older docs all linked `/me/leaves`.
 * Exported for isolation testing (#379 pattern).
 */
export function MeLeavesTasksRedirect() {
  return <Navigate to="/me/tasks" replace />;
}

/**
 * W2-B: invite-token preservation across auth.
 *
 * `<InvitePreviewPage/>` (the `/invite/:token` unauth preview from Tom
 * F1) stashes the token in `sessionStorage["roster.invite_pending"]`
 * before sending the user into Clerk. Without a consumer the value
 * would sit there forever and post-auth Clerk would land the user at
 * `/`, dropping the invite intent on the floor (the bug
 * `wave-1-tester-walkthrough.md` queued for W2-B).
 *
 * `consumePendingInviteRedirect()` runs once per route-evaluation tick
 * inside the `accountId === present` branches of SignInGate / SignUpGate
 * / RequireAuth. It pops the stashed `returnTo` (canonical form
 * `/invite/<token>/accept` post Tom F1), validates the shape, and
 * returns the path so the caller can `<Navigate to=... replace/>` to
 * land the user back on the invite-accept flow. If the value is
 * malformed or missing it returns null and the caller falls through
 * to the existing `/` default.
 *
 * Pre-Tom-F1, `InviteAcceptPage` itself wrote `returnTo: /invite/<token>`
 * (no `/accept` suffix) and handled the pre-auth bounce internally.
 * The shape-check below still accepts that legacy form so any in-
 * flight stash survives the rollout — both `/invite/<token>` and
 * `/invite/<token>/accept` start with `/invite/`.
 *
 * Why sessionStorage (not search-param): the writer (preview page)
 * and reader (this helper) both read from the same key; mirroring
 * makes the contract a single source of truth. Search-params survive
 * only one Clerk hop, sessionStorage survives the full sign-up +
 * email-verify + sign-in choreography.
 */
export function consumePendingInviteRedirect(): string | null {
  if (typeof window === "undefined") return null;
  let raw: string | null = null;
  try {
    raw = window.sessionStorage.getItem("roster.invite_pending");
  } catch {
    // Private-mode Safari etc. — graceful no-op.
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { token?: unknown; returnTo?: unknown };
    const token = typeof parsed.token === "string" ? parsed.token : null;
    const returnTo = typeof parsed.returnTo === "string" ? parsed.returnTo : null;
    // Clear regardless of validation outcome — a corrupt blob shouldn't
    // pin the user in a redirect loop.
    window.sessionStorage.removeItem("roster.invite_pending");
    // Validate shape: returnTo must point at /invite/<token-ish>. We
    // never accept arbitrary URLs to avoid open-redirect on stale data.
    if (!token || !returnTo) return null;
    if (!returnTo.startsWith("/invite/")) return null;
    return returnTo;
  } catch {
    try {
      window.sessionStorage.removeItem("roster.invite_pending");
    } catch {
      /* no-op */
    }
    return null;
  }
}

/**
 * Issue #6 — Clerk's default widget renders the upstream instance
 * name + Clerk's palette, which reads as "this is built on a third-
 * party identity service" right at the moment a prospect is about
 * to type their team's data. The `appearance` overrides below pin
 * the widget to Tarii's deep-teal accent (mirrors `--color-accent`
 * in `tokens.css`) and stamp the Tarii wordmark inside the card so
 * the heading line reads as Tarii rather than the Clerk instance.
 *
 * The "Secured by Clerk" footer is plan-locked on Clerk's free
 * tier and is intentionally out of scope here — that's a billing
 * decision, not an engineering one.
 *
 * Since the custom sign-in pages (3 Oct 2026) this styles only the
 * classic fallback card (`?classic=1` and Clerk's own sub-paths).
 */
export const tariiClerkAppearance = {
  variables: {
    colorPrimary: "#1F7A6E",
    colorTextOnPrimaryBackground: "#FFFFFF",
    borderRadius: "0.5rem",
    fontFamily: "inherit",
  },
  layout: {
    logoImageUrl: "/tarii-logo.png",
    logoPlacement: "inside",
  },
} as const;

/**
 * Custom sign-in (3 Oct 2026) — which auth surface a `/sign-in/*` or
 * `/sign-up/*` URL gets in Clerk mode.
 *
 *   - `custom`   — Tarii's own page (`routes/auth/`): the bare path.
 *   - `continue` — the same page resuming an attempt Clerk left on the
 *                  client (`/sign-in/continue`, `/sign-up/continue`; the
 *                  Google callback routes leftover steps there).
 *   - `classic`  — Clerk's prebuilt card, exactly as before. Reached by
 *                  `?classic=1` — a SAFETY NET FOR ONE RELEASE, to be
 *                  removed once the custom pages have held up in prod —
 *                  and by every other sub-path. Those (`factor-one`,
 *                  `factor-two`, `reset-password`, `verify-email-address`,
 *                  `sso-callback`, `tasks/…`) are the card's own internal
 *                  routes: its Clerk-driven step changes are full page
 *                  loads that drop `?classic=1`, and session tasks land on
 *                  `/sign-in/tasks`. The card resumes the attempt in
 *                  flight, or returns to its start — the bare path, i.e.
 *                  the custom page. Nothing under these paths 404s.
 */
export type AuthSurface = "custom" | "continue" | "classic" | "add" | "choose";

/**
 * #3005 — two more sign-in surfaces, open to a signed-in person too:
 *   - `add`    — `/sign-in/add`, **Add another account**;
 *   - `choose` — `/sign-in/choose`, **Choose an account**.
 */
export function resolveAuthSurface(pathname: string, search: string, base: string): AuthSurface {
  if (new URLSearchParams(search).get("classic") === "1") return "classic";
  const rest = pathname.slice(base.length).replace(/^\/+|\/+$/g, "");
  if (rest === "") return "custom";
  if (rest === "continue") return "continue";
  if (base === "/sign-in" && rest === "add") return "add";
  if (base === "/sign-in" && rest === "choose") return "choose";
  return "classic";
}

/**
 * Sign-in landing. Already-authenticated users get bounced to `/`
 * (or back to `/invite/:token` if they came in via an invite link —
 * see `consumePendingInviteRedirect`). That bounce is also how the
 * custom page "finishes": after `setActive` the session reaches
 * `AuthContext`, this gate re-renders, and the same branch sends the
 * person on — so post-auth routing is unchanged.
 * #2240 — "on" is the address they opened when there is one: the
 * `?redirect_url=` this URL carries (also stashed here, so Google's
 * return to `/` still finds it) or the tab's stash (`lib/returnTo`),
 * validated same-origin.
 * Unauthenticated:
 *   - Clerk mode → Tarii's own `<SignInPage/>` (custom, 3 Oct 2026), or
 *     Clerk's `<SignIn/>` card with Tarii branding via
 *     `tariiClerkAppearance` (issue #6) — see `resolveAuthSurface`.
 *   - Dev-shim   → render the user-picker.
 * #3005 — `/sign-in/add` and `/sign-in/choose` never send a signed-in
 * person away: adding a second account and picking one are what they
 * are for. The dev shim gets the picker in add mode.
 */
export function SignInGate() {
  const { accountId, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullPageLoading />;
  const surface = resolveAuthSurface(location.pathname, location.search, "/sign-in");
  if (surface === "choose") return <ChooseAccountPage />;
  if (surface === "add") return hasClerk ? <SignInPage mode="add" /> : <UserPicker mode="add" />;
  const carried = returnTargetFromSearch(location.search);
  if (accountId) {
    const invitePath = consumePendingInviteRedirect();
    const stashed = consumeReturnTo();
    return <Navigate to={safeReturnTarget(invitePath) ?? carried ?? stashed ?? "/"} replace />;
  }
  if (carried) stashReturnTo(carried);
  if (hasClerk) {
    if (surface !== "classic") return <SignInPage resume={surface === "continue"} />;
    return (
      <main className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center gap-6 px-4 py-12">
        <header className="flex flex-col items-center gap-2 text-center">
          <h1 className="text-2xl font-semibold text-fg">Sign in to Tarii</h1>
          <p className="text-sm text-fg-muted">
            Worker-friendly rostering. Your team's data stays with your team.
          </p>
        </header>
        <SignIn
          signUpUrl="/sign-up"
          routing="path"
          path="/sign-in"
          appearance={tariiClerkAppearance}
        />
      </main>
    );
  }
  return <UserPicker />;
}

/**
 * #1720 — the optional intent strip above the sign-up form (Tarii's own
 * page since 3 Oct 2026; the classic Clerk card at `?classic=1`).
 *
 * Round 4 made `/start` the page the landing's Sign up CTA leads to,
 * so most arrivals here have ALREADY answered the create-or-join
 * question — the strip renders only when no intent is stashed (a
 * direct `/sign-up` visit, or the sign-in page's own sign-up link).
 * When a door was taken, a one-line confirmation with a "Change" link
 * back to `/start` renders instead — never ask the question twice.
 *
 * It writes `tarii.signup_intent` and nothing else — no routing, no
 * validation, no bearing on what Clerk does next. If storage is
 * unavailable, or the person skips it, the post-auth chooser asks
 * exactly as before.
 *
 * Deliberately quiet: a hairline-ruled strip with two small toggles.
 * The sign-up form stays the focal component of this page
 * (`feedback_ui_section_focal_component.md`) — this is a preference,
 * not a step.
 */
const INTENT_LABELS: Record<SignupIntent, string> = {
  create: "You're setting up a new team.",
  join: "You're joining an existing team.",
};

function SignUpIntentStrip() {
  // Read once at mount — the stash only changes via this strip or
  // `/start`, both of which re-mount this surface on the way back.
  const [stashed] = useState<SignupIntent | null>(() => peekSignupIntent());
  const [picked, setPicked] = useState<SignupIntent | null>(null);

  if (stashed) {
    return (
      <p
        className="w-full border-border border-y py-3 text-fg-muted text-sm"
        data-testid="signup-intent-confirm"
      >
        {INTENT_LABELS[stashed]}{" "}
        <Link to="/start" className="text-accent-fg underline underline-offset-4">
          Change
        </Link>
      </p>
    );
  }

  function pick(intent: SignupIntent) {
    setPicked(intent);
    stashSignupIntent(intent);
  }

  const options: ReadonlyArray<{ intent: SignupIntent; label: string }> = [
    { intent: "create", label: "Setting up a new team" },
    { intent: "join", label: "Joining an existing team" },
  ];

  return (
    <section
      aria-label="What brings you to Tarii"
      className="w-full border-border border-y py-4"
      data-testid="signup-intent-strip"
    >
      <span className="font-mono text-[10px] text-fg-subtle uppercase tracking-[0.18em]">
        Optional
      </span>
      <p className="mt-1.5 text-fg-muted text-sm">Which one are you here for?</p>
      <div className="mt-3 flex flex-wrap gap-2">
        {options.map((option) => (
          <Button
            key={option.intent}
            type="button"
            size="sm"
            variant={picked === option.intent ? "primary" : "outline"}
            aria-pressed={picked === option.intent}
            onClick={() => pick(option.intent)}
          >
            {option.label}
          </Button>
        ))}
      </div>
      {picked ? (
        <p className="mt-3 text-fg-subtle text-xs">
          We'll take you straight there once your account is created.
        </p>
      ) : null}
    </section>
  );
}

export function SignUpGate() {
  const { accountId, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullPageLoading />;
  if (accountId) {
    const invitePath = consumePendingInviteRedirect();
    return <Navigate to={safeReturnTarget(invitePath) ?? consumeReturnTo() ?? "/"} replace />;
  }
  if (hasClerk) {
    const surface = resolveAuthSurface(location.pathname, location.search, "/sign-up");
    if (surface !== "classic") {
      return (
        <SignUpPage
          resume={surface === "continue"}
          intentStrip={surface === "custom" ? <SignUpIntentStrip /> : undefined}
        />
      );
    }
    return (
      <main className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center gap-6 px-4 py-12">
        <header className="flex flex-col items-center gap-2 text-center">
          <h1 className="text-2xl font-semibold text-fg">Create your Tarii account</h1>
          <p className="text-sm text-fg-muted">
            Worker-friendly rostering. Your team's data stays with your team.
          </p>
        </header>
        <SignUpIntentStrip />
        <SignUp
          signInUrl="/sign-in"
          routing="path"
          path="/sign-up"
          appearance={tariiClerkAppearance}
        />
      </main>
    );
  }
  // Dev-shim: there's no sign-up — bounce to the picker.
  return <Navigate to="/sign-in" replace />;
}

/**
 * `/sso-callback` — where Google returns to (custom sign-in, 3 Oct
 * 2026). Deliberately NOT bounced when a session appears: Clerk's
 * callback is mid-flight and does its own navigation once it has set
 * the session. Dev-shim has no Google, so it goes to the picker.
 */
export function SsoCallbackGate() {
  if (!hasClerk) return <Navigate to="/sign-in" replace />;
  return <SsoCallbackPage />;
}

/**
 * `<RequireAuthOrLanding/>` — auth gate with a calm marketing fallback
 * on the root path.
 *
 * Issue 002 (`docs/issues/002-incognito-auto-shims-demo-persona.md`):
 * an unauthenticated visitor to `/` previously redirected straight to
 * `/sign-in` (the Clerk widget) which felt like the product was
 * gated behind a wall. Worse, when a stale dev-shim id was in
 * localStorage from an earlier `/demo` visit, the visitor was
 * silently signed in as the demo lead persona. The AuthContext
 * change pins the dev-shim to `/demo` only; this gate adds the
 * calm landing fallback so a real prospect on the marketing root
 * sees what Tarii is + two CTAs (Sign in / Try the demo) before
 * any auth challenge.
 *
 * Behaviour matrix (post-fix):
 *
 *   loading ............................. → spinner
 *     (Clerk mode: includes "signed in to Clerk, account not loaded yet")
 *   authenticated ....................... → existing outlet (AppShell + page)
 *   Clerk session, account failed ....... → <AccountUnavailable/> in place
 *     (#2240: never `/sign-in` — the person is signed in; the address stays)
 *   unauthenticated, path === "/" ....... → <LandingPage/>
 *   unauthenticated, deep route ......... → <Navigate to="/sign-in?redirect_url=…"/>
 *     (#2071 / #2240: the address, query and all, is carried on the URL
 *     and stashed, and returned to after sign-in — `lib/returnTo.ts`)
 *
 * The post-Clerk-auth invite-redirect logic is unchanged — it runs
 * only on the authenticated branch (an unauth visitor with a queued
 * invite intent is sent through `/sign-in` first, where SignInGate's
 * own `consumePendingInviteRedirect()` call lands them back on the
 * accept flow after auth).
 */
export function RequireAuthOrLanding() {
  const { accountId, loading, sessionWithoutAccount, refresh, signOut, accounts } = useAuth();
  const location = useLocation();
  // Signed in for real: drop the ended-demo flags and the browser record. A
  // live demo session also has an account id, and it keeps its record.
  useEffect(() => {
    if (accountId && !hasStoredDemoSession()) clearDemoEnded();
  }, [accountId]);
  if (loading) return <FullPageLoading />;
  if (!accountId) {
    // #2240 — signed in to Clerk, but the account didn't load (a tab back
    // from a long idle, the API briefly unreachable). Not signed out: say
    // so here and keep the address, rather than bouncing to `/sign-in`.
    if (sessionWithoutAccount) {
      return (
        <AccountUnavailable
          onRetry={() => void refresh()}
          // #3005 — this account only; Choose an account while another stays.
          onSignOut={() =>
            void signOut({
              redirectUrl: afterSignOutPath(accounts, "current", "/sign-in"),
              scope: "current",
            })
          }
        />
      );
    }
    if (location.pathname === "/") {
      return <LandingPage />;
    }
    // #2986 — a demo visitor whose session ended has no account to sign in
    // to: send them to the demo start, with no return address.
    if (isDemoEnded()) return <Navigate to="/demo" replace />;
    // #2071 / #2240 — the address (path + query) comes back after sign-in.
    const here = `${location.pathname}${location.search}${location.hash}`;
    stashReturnTo(here);
    return <Navigate to={signInPathFor(here)} replace />;
  }
  // W2-B: catch the post-Clerk-auth landing. Clerk's default after-
  // sign-in URL is `/`, which lands here; if a `roster.invite_pending`
  // is queued (set by InviteAcceptPage before the auth bounce), redirect
  // back to `/invite/:token` so the accept flow completes. One-shot —
  // `consumePendingInviteRedirect` clears the key on read.
  const invitePath = safeReturnTarget(consumePendingInviteRedirect());
  if (invitePath) return <Navigate to={invitePath} replace />;
  // #2071 — Google's return lands on `/`; carry on to the stashed address.
  const returnTo = safeReturnTarget(consumeReturnTo());
  if (returnTo && returnTo !== `${location.pathname}${location.search}`) {
    return <Navigate to={returnTo} replace />;
  }
  return <Outlet />;
}

/**
 * `<MemberWelcomeGate/>` — first-time-member welcome redirect.
 *
 * Closes Tom (member) UX log F2 P0: a member completing sign-up + first
 * sign-in lands on `/teams/<id>` with the AppShell chrome and a
 * "Member" chip — no privacy preamble. The trust pillar is built (per
 * `feedback_privacy_assurance_for_team_usage.md`) but invisible at
 * first paint.
 *
 * This wrapper sits between `<RequireAuth/>` and `<AppShell/>`. When
 * the active workspace's role is `"member"` AND the worker has not yet
 * dismissed the three-circles privacy interstitial for this account on
 * this browser, redirect once to `/me/welcome`. After dismissal (the
 * worker clicks "Get started" or "Skip for now"), the gate falls
 * through to the AppShell outlet on every subsequent render.
 *
 * Why role-aware (rather than universal first-render): leads have
 * their own onboarding flow (`/onboarding` — three-step team-creation
 * wizard) and have already seen Tarii's chrome by the time they reach
 * a `/teams/<id>` page. Members arrive via an invite-accept flow that
 * skips `/onboarding` entirely — which is correct (they're not
 * creating a team) but leaves them with no Tarii-shaped welcome.
 *
 * Why a wrapper (rather than a useEffect inside AppShell): keeping the
 * redirect at the route-tree level means the worker never paints the
 * AppShell on first entry — they go straight from RequireAuth → /me/welcome.
 * The fallback rule in the brief calls this out as the safer pattern
 * (no race condition with auth-state hydration).
 *
 * Same localStorage key as the MeAudit interstitial
 * (`tarii.privacy.three_circles.dismissed.<accountId>`) — dismissing
 * either surface dismisses both, so a worker sees one trust-pillar
 * moment per session, not two.
 */
function MemberWelcomeGate() {
  const { accountId } = useAuth();
  const location = useLocation();
  const workspacesQuery = useWorkspaces();

  // While the workspaces query loads, render the outlet — the lead-side
  // pages don't need the welcome surface and the loading flash would be
  // worse than the fallback. Once workspaces resolve, the role-aware
  // redirect runs on the next render tick.
  if (!workspacesQuery.data) {
    return <Outlet />;
  }

  // The role of the workspace the AppShell WOULD resolve to — stored id
  // if still valid, else the most-recently-active one. Reading the stored
  // key raw (and falling through when it was empty) skipped the welcome
  // for every member on a fresh browser or after a sign-out: the landing
  // gate sends them to `/me`, whose frame never mounts the AppShell
  // resolver that would have written the key (30 Sep 2026 re-click).
  const role = resolveWelcomeGateRole(workspacesQuery.data, location.pathname);

  // Only members get the welcome interstitial. Leads have OnboardingPage;
  // co-leads inherit the lead chrome (founder §4.1).
  if (role !== "member") {
    return <Outlet />;
  }

  // Already dismissed → skip the redirect, render the outlet.
  if (readPrivacyDismissed(accountId)) {
    return <Outlet />;
  }

  // #2071 — a member who followed the Telegram chat's Connect link finishes
  // connecting first; the welcome waits for their next visit.
  if (location.pathname === CONNECT_TELEGRAM_PATH) {
    return <Outlet />;
  }

  // Avoid a redirect loop if the worker is already on /me/welcome (the
  // route is mounted outside the gate so this branch won't fire in
  // practice, but the guard is cheap insurance).
  if (location.pathname === "/me/welcome") {
    return <Outlet />;
  }

  return <Navigate to="/me/welcome" replace />;
}

/**
 * `<MemberLandingGate/>` — member-first landing (#1682, founder prod
 * inspection finding 5).
 *
 * Clerk's after-sign-in URL is `/`, which used to land every returning
 * member on the desktop HomePage team picker: the only member-persona
 * routing was `<MemberWelcomeGate/>`'s ONE-SHOT welcome interstitial,
 * so after the first dismissal a member was dumped into desktop chrome
 * on every sign-in. This gate is the persistent counterpart — it does
 * NOT read the privacy-dismissed flag:
 *
 *   * An account whose every workspace role is `member` visiting `/`
 *     is sent to `/me` — the ONE cross-team member interface (the
 *     memberapp data hooks are account-wide, so all teams' duties are
 *     already there; no team picker needed).
 *   * Dual-role accounts (any `lead` / `co_lead` workspace) keep the
 *     desktop `/` untouched — including the ProfileSheet "Lead view"
 *     escape, which navigates here on purpose.
 *   * Accounts with no workspaces yet fall through (HomePage owns the
 *     create-or-join empty state).
 *
 * Mounted BETWEEN `<MemberWelcomeGate/>` and `<AppShell/>` so the
 * first-run ordering pinned by the #1652 regression tests is
 * untouched (an undismissed member still hits `/me/welcome` first) and
 * a member-only account never paints AppShell chrome on `/`. While
 * workspaces load, the outlet renders — same trade the welcome gate
 * makes (a loading flash beats blocking every lead page).
 */
/**
 * #1720 — where does `/` go for an account with NO team?
 *
 * Before this issue: the `HomePage` zero-team empty state, whose one
 * action was "Start onboarding" — a silent default into founding a
 * tenant. A member who signed up before their lead's invite arrived got
 * no join affordance at all. Now the account is asked, once, and the
 * answer is explicit.
 *
 * Resolution order — the precedence contract this issue pins:
 *
 *   1. `roster.invite_pending` — ALWAYS wins. It is consumed one level
 *      up in `<RequireAuthOrLanding/>`, which navigates to the accept
 *      flow before this hook ever runs. Someone who followed an invite
 *      link is going to that team, whatever they clicked earlier.
 *   2. `tarii.signup_intent` — the create-or-join hint stashed before
 *      auth (landing → `/sign-up` strip). Consumed here, one-shot:
 *      `create` → `/onboarding`, `join` → `/join`, skipping the
 *      chooser for someone who has already answered it.
 *   3. No intent → `/start`, the chooser.
 *
 * Scope: only the root path, and only while the account has zero
 * workspaces. A zero-team account deep-linking elsewhere keeps today's
 * behaviour, and the moment they hold one membership this is inert.
 * While the workspace list is still loading we render through — same
 * trade `<MemberLandingGate/>` makes (a loading flash beats blocking
 * every authenticated page on one query).
 *
 * The decision is cached in a ref so a re-render cannot re-consume the
 * (now cleared) intent key and downgrade `/onboarding` to `/start`
 * mid-navigation.
 */
function useZeroTeamStartTarget(): string | null {
  const location = useLocation();
  const workspacesQuery = useWorkspaces();
  const { account, loading: accountLoading } = useAuth();
  const decidedRef = useRef<string | null>(null);

  if (location.pathname !== "/") return null;
  const workspaces = workspacesQuery.data;
  if (!workspaces || workspaces.length > 0) return null;
  // #2060 — an admin account with no team of its own is the master
  // account: its Home is the all-teams table, not the create-or-join
  // chooser. Wait for `/api/account/me` before deciding.
  if (decidedRef.current === null && accountLoading && !account) return null;
  if (account?.is_admin === true) return null;

  if (decidedRef.current === null) {
    const intent = consumeSignupIntent();
    decidedRef.current =
      intent === "create" ? "/onboarding" : intent === "join" ? "/join" : "/start";
  }
  return decidedRef.current;
}

/**
 * #1720 — the single entry decision for `/`, composed in order.
 *
 * Two questions share this slot and they are asked in a fixed sequence:
 * first "does this account have a team at all?" (the create-or-join
 * branch), then "is this account member-only?" (the `/me` landing from
 * #1682). Composing them in one route element rather than nesting a
 * second wrapper route keeps the route table's shape — and its
 * indentation — unchanged, which matters on a file this many surfaces
 * reach into. `<MemberLandingGate/>` is untouched and still exported
 * for its own isolation tests; its `<Outlet/>` resolves to this route's
 * children exactly as before.
 */
export function HomeEntryGate() {
  const startTarget = useZeroTeamStartTarget();
  if (startTarget) return <Navigate to={startTarget} replace />;
  return <MemberLandingGate />;
}

export function MemberLandingGate() {
  const location = useLocation();
  const workspacesQuery = useWorkspaces();
  const workspaces = workspacesQuery.data;
  if (
    workspaces &&
    workspaces.length > 0 &&
    location.pathname === "/" &&
    workspaces.every((w) => w.role === "member")
  ) {
    return <Navigate to="/me" replace />;
  }
  return <Outlet />;
}

function FullPageLoading() {
  return (
    <main className="flex min-h-screen items-center justify-center bg-bg text-fg-muted">
      <p className="text-sm">Loading…</p>
    </main>
  );
}

/**
 * #2240 — the person is signed in (Clerk holds the session) but Tarii
 * could not load their account, even after retrying. They came to open
 * a page, so the page's address stays in the bar and one button tries
 * again; nothing here sends them to sign in. A quiet Sign out stays as
 * the way out (an account the API keeps refusing is never a dead end).
 * Exported for tests.
 */
export function AccountUnavailable({
  onRetry,
  onSignOut,
}: {
  onRetry: () => void;
  onSignOut: () => void;
}) {
  return (
    <main className="flex min-h-screen items-center justify-center bg-bg px-4">
      <div className="flex max-w-sm flex-col items-center gap-4 text-center">
        <h1 className="font-semibold text-fg text-lg">We couldn't load your account</h1>
        <p className="text-fg-muted text-sm">
          You're still signed in. This page opens as soon as Tarii answers.
        </p>
        <Button type="button" onClick={onRetry}>
          Try again
        </Button>
        <button
          type="button"
          onClick={onSignOut}
          className="text-fg-muted text-sm underline underline-offset-4 hover:text-fg"
        >
          Sign out
        </button>
      </div>
    </main>
  );
}

/**
 * WS6 OPT-2 (refs #823) — Suspense fallback for the lazily-loaded route
 * chunks. Mirrors the `PageSkeleton` shape in `routes/CycleRoutes.tsx`
 * (heading + content skeletons) so a first route visit shows a calm
 * placeholder, not a blank flash, while the route's JS chunk loads.
 */
function RouteSuspenseFallback() {
  return (
    <div className="flex flex-col gap-4 p-6">
      <Skeleton className="h-10 w-2/3" />
      <Skeleton className="h-32" />
      <Skeleton className="h-64" />
    </div>
  );
}

function NotFound() {
  useBreadcrumbs([{ label: "Home", to: "/" }, { label: "Not found" }]);
  return <NotFoundPage />;
}

/** #2030 — an unknown address under a team's Setup (e.g. the guessed
 *  `/setup/imports`; the real one is `/setup/workbooks`) renders inside the
 *  Setup layout, its section list still on screen, with a way back. */
function SetupNotFound() {
  const { teamId = "" } = useParams<{ teamId: string }>();
  return <NotFoundPage backTo={`/teams/${teamId}/setup/stations`} backLabel="Back to Setup" />;
}
